import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, FileCheck2, FileText, FlaskConical, Laptop } from 'lucide-react';
import Screen from '../demo/Screen';
import { STEPS } from '../demo/steps';
import './Home.css';

/*
 * On arrival Home plays a ~6.5 s "screen loading" build-in, then rests: the top
 * nav fades down, the headline words rise, the hero collage is revealed (soft curtain, scale-in, a shine sweep and a few
 * sparkles), the stats cards slide up one by one with their numbers counting
 * up, and "Start the journey" (to their right) pops in with its "Best viewed on a laptop" note. Transform and opacity
 * only, so nothing shifts. The <main> carries data-trapezium-state "loading" while it plays and
 * "ready" once it rests. Any key or pointer press finishes it at once, and
 * under reduced motion it starts at rest. The timings live in Home.css; the
 * JS only needs the total and the count-up windows.
 */
export const HOME_BUILD_MS = 6500;
const COUNT_START_MS = 4450; // first stats card's count-up starts here
const COUNT_STAGGER_MS = 280; // matches the cards' slide-up stagger in Home.css
const COUNT_MS = 1000;

/* Product and business numbers. `suffix` follows the count-up (10 → "10M+"). */
const STATS = [
  { key: 'biomarkers', icon: FlaskConical, tone: 'blue', value: '1,500+', count: 1500, label: 'biomarkers' },
  { key: 'profiles', icon: FileText, tone: 'teal', value: '30+', count: 30, label: 'test profiles' },
  {
    key: 'reports',
    icon: FileCheck2,
    tone: 'purple',
    value: '10M+',
    count: 10,
    suffix: 'M+',
    label: 'reports delivered',
  },
  { key: 'clients', icon: Building2, tone: 'amber', value: '40+', count: 40, label: 'clients' },
];

/* The hero collage (three report pages and the patient app on a desk) is the mockup's artwork, cropped clean. */
const COLLAGE_SRC = '/demo/home/hero-collage.jpg';
const COLLAGE_ALT =
  'Sample Smart Health Report pages for a demo patient, Rohan Mehta: blood counts and a lipid profile, the ' +
  'Smart Health Report cover, metabolic health with diet recommendations, and the patient app showing a ' +
  'wellness score of 78.';

/* Decorative sparkles over the collage while it assembles: [left %, top %, size px]. */
const SPARKLES = [
  [31, 22, 26],
  [57, 14, 20],
  [69, 62, 28],
  [85, 26, 22],
  [45, 80, 18],
];

const prefersStill = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const formatCount = (n) => n.toLocaleString('en-US');

/*
 * Counts 0 → `target` over COUNT_MS, starting `delayMs` after mount, while
 * `active`. Returns null when not counting (the real value shows).
 */
function useCountUp(target, delayMs, active) {
  const [shown, setShown] = useState(active && target ? 0 : null);
  useEffect(() => {
    if (!active || !target) {
      setShown(null);
      return undefined;
    }
    let interval;
    let startedAt;
    const tick = () => {
      const t = Math.min(1, (Date.now() - startedAt) / COUNT_MS);
      if (t >= 1) {
        clearInterval(interval);
        setShown(null);
      } else {
        setShown(Math.round(target * (1 - (1 - t) ** 3)));
      }
    };
    const timeout = setTimeout(() => {
      startedAt = Date.now();
      interval = setInterval(tick, 32);
    }, delayMs);
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [target, delayMs, active]);
  return shown;
}

function Stat({ index, icon: Icon, tone, value, count, suffix = '+', label, building }) {
  const shown = useCountUp(count, COUNT_START_MS + index * COUNT_STAGGER_MS, building);
  const counting = shown !== null;
  return (
    <li style={{ '--i': index }}>
      <span className={`icon-tile icon-tile--${tone} home-stats__icon`} aria-hidden="true">
        <Icon size={20} strokeWidth={2} />
      </span>
      <span className="home-stats__text">
        <strong className={counting ? 'is-counting' : undefined}>
          {/* The real value always holds the space and the text; the counter is drawn over it. */}
          <span className="home-stats__value">{value}</span>
          {counting ? (
            <span className="home-stats__count" aria-hidden="true">
              {`${formatCount(shown)}${suffix}`}
            </span>
          ) : null}
        </strong>
        <span>{label}</span>
      </span>
    </li>
  );
}

/* Each word rises on its own; the text (and so the heading's name) is unchanged. */
function Words({ text, from }) {
  return text.split(' ').map((word, i, all) => (
    <span key={word}>
      <span className="home-word" style={{ '--w': from + i }}>
        {word}
      </span>
      {i < all.length - 1 ? ' ' : null}
    </span>
  ));
}

export default function Home() {
  const [building, setBuilding] = useState(() => !prefersStill());

  useEffect(() => {
    if (!building) return undefined;
    const finish = () => setBuilding(false);
    const timer = setTimeout(finish, HOME_BUILD_MS);
    window.addEventListener('keydown', finish);
    window.addEventListener('pointerdown', finish);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', finish);
      window.removeEventListener('pointerdown', finish);
    };
  }, [building]);

  return (
    <Screen
      id="home"
      title="Clearer reports"
      className={building ? 'home is-building' : 'home'}
      state={building ? 'loading' : 'ready'}
    >
      <header className="home-hero">
        <h1 className="headline home-hero__headline">
          <Words text="Clearer reports." from={0} />{' '}
          <em>
            <Words text="Better patient experiences." from={2} />
          </em>
        </h1>
      </header>

      <div className="home-hero__stage">
        <img
          className="home-collage"
          src={COLLAGE_SRC}
          alt={COLLAGE_ALT}
          width="1409"
          height="516"
          decoding="async"
        />
        {building ? (
          <span className="home-sparkles" aria-hidden="true">
            {SPARKLES.map(([left, top, size], i) => (
              <span
                key={`${left}-${top}`}
                className="home-sparkle"
                style={{ left: `${left}%`, top: `${top}%`, '--s': `${size}px`, '--i': i }}
              />
            ))}
          </span>
        ) : null}
      </div>

      {/* The compact stats strip and, to its right, the way in (2026-10-24: one row). */}
      <div className="home-foot">
        <ul className="home-stats">
          {STATS.map(({ key, ...stat }, index) => (
            <Stat key={key} index={index} building={building} {...stat} />
          ))}
        </ul>

        {/* Step 1 is gated (App.js): a visitor who hasn't signed up meets the form first, then lands on step 1. */}
        <div className="home-start">
          <Link
            to={STEPS[0].route}
            className="btn btn--primary home-hero__cta"
            data-trapezium-id="home.start-journey"
          >
            Start the journey
            <ArrowRight size={18} aria-hidden="true" />
          </Link>
          <p className="home-start__note">
            <Laptop size={14} aria-hidden="true" />
            Best viewed on a laptop
          </p>
        </div>
      </div>
    </Screen>
  );
}
